import { createSelector } from '@reduxjs/toolkit';
// Types
import Reducers from 'app/types/Reducers';
// Models
import IManagementAccount from 'app/models/ManagementAccount';
// Models
import { RootState } from 'app/store';
import IBillingInvoice from 'app/models/BillingInvoice';
// Selectors
import { selectAccounts } from 'app/store/AccountsManagement/AccountsManagement.selectors';
// Utilities
import { sortByName, sortByNumber } from 'app/utilities/SortBy';

export const selectInvoices = (state:RootState) => state[Reducers.BillingInvoices].invoices;
export const selectInvoicesSummary = (state:RootState) => state[Reducers.BillingInvoices].invoicesSummary;
export const selectInvoicesSummaryBreakdown = (state:RootState) => state[Reducers.BillingInvoices].invoicesSummaryBreakdown;
export const selectInvoicesSummaryBreakdownByAccount = (state:RootState) => state[Reducers.BillingInvoices].invoicesSummaryBreakdownByAccount;
export const selectInvoicesSummaryBreakdownByAccountSort = (state:RootState) => state[Reducers.BillingInvoices].invoicesSummaryBreakdownByAccountSort;
export const selectInvoicesTotal = (state:RootState) => state[Reducers.BillingInvoices].invoicesTotal;
export const selectParams = (state:RootState) => state[Reducers.BillingInvoices].params;
export const selectBatchInvoices = (state:RootState) => state[Reducers.BillingInvoices].batchInvoices;
export const selectLoading = (state:RootState) => state[Reducers.BillingInvoices].loading;
export const selectInterimInvoices = (state:RootState) => state[Reducers.BillingInvoices].interimInvoices;
export const selectInvoicesExpandedAll = (state:RootState) => state[Reducers.BillingInvoices].invoicesExpandedAll;

export const selectInterimInvoicesAmount = createSelector(
  [ selectInterimInvoices ],
  ( interimInvoices ) => {
    if ( !interimInvoices || !interimInvoices.length ) return 0;
    return interimInvoices.reduce(( total, invoice ) => total + ( invoice.amount || 0 ), 0);
  }
);

export const selectBatchInvoicesIds = createSelector(
  [ selectBatchInvoices ],
  ( batchInvoices ) => {
    if ( !batchInvoices || !batchInvoices.length ) return [];
    return batchInvoices.map((invoice) => invoice.id);
  }
);

export const selectBatchSentInvoices = createSelector(
  [ selectBatchInvoices ],
  ( batchInvoices ) => {
    if ( !batchInvoices || !batchInvoices.length ) return [];
    return batchInvoices
      .filter((invoice) => invoice.status === 'sent');
  }
);

export const selectHasSentBatchInvoices = createSelector(
  [selectBatchInvoices],
  (batchInvoices) => {
    if (!batchInvoices || !batchInvoices.length) return false;
    return batchInvoices.some((invoice) => invoice.status === 'sent');
  }
);

export const selectIsInvoiceIdInBatch = createSelector(
  [
    selectBatchInvoicesIds,
    (_, props:{ invoiceId:number }) => props
  ],
  ( batchInvoicesIds:number[], { invoiceId } ) => batchInvoicesIds.includes(invoiceId)
);

export const selectBatchInvoicesAmount = createSelector(
  [selectBatchInvoices],
  (batchInvoices) => {
    if (!batchInvoices.length) return null;

    const paidInvoices = batchInvoices.filter((invoice) => invoice.status === 'paid');
    const outstandingInvoices = batchInvoices.filter((invoice) => invoice.status !== 'paid');

    const paidAmount = paidInvoices.reduce((total, invoice) => total + (invoice.amount || 0), 0);
    const outstandingAmount = outstandingInvoices.reduce((total, invoice) => total + (invoice.amount || 0), 0);

    const result: Record<string, number> = {};
    if (paidInvoices.length) result.paid = paidAmount;
    if (outstandingInvoices.length) result.outstanding = outstandingAmount;

    return Object.keys(result).length ? result : null;
  }
);

export const selectBatchInvoicesOnPage = createSelector(
  [selectInvoices, selectBatchInvoicesIds],
  (invoices, batchInvoicesIds) => {
    if (!invoices) return [];
    return invoices.filter((invoice:IBillingInvoice) => batchInvoicesIds.includes(invoice.id));
  }
)

export const selectAreAllOnPageSelected = createSelector(
  [selectBatchInvoicesOnPage, selectInvoices],
  (selectedInvoicesOnPage, invoices) => {
    if (!invoices || !selectedInvoicesOnPage) return false;
    return selectedInvoicesOnPage.length === invoices.length && invoices.length > 0;
  }
)

export const selectIsSomeOnPageSelected = createSelector(
  [selectBatchInvoicesOnPage, selectAreAllOnPageSelected],
  (selectedInvoicesOnPage, areAllOnPageSelected) => {
    if (!selectedInvoicesOnPage) return false;
    if (areAllOnPageSelected) return false;
    return selectedInvoicesOnPage.length > 0;
  }
)

export const selectAccountFromParams = createSelector(
  [
    selectAccounts,
    selectParams
  ],
  (accounts:IManagementAccount[] | null, params:any) => {
    if ( !accounts || !params.accountId ) return undefined;
    return accounts.find((account:IManagementAccount) => account.id === params.accountId);
  }
);

interface IValidationErrors {
  startError?: string;
  endError?: string;
  rangeError?: string;
}

export const selectValidateDateRange = createSelector(
  [
    selectParams,
    (_, props:{ isRequired: boolean }) => props
  ],
  ( params, props ) => {
    const start = params['dateRangeStart'];
    const end = params['dateRangeEnd'];
    const errors: IValidationErrors = {};
    // Check if start date is valid
    if ( !start && props.isRequired ){
      errors.startError = 'Start date is required.';
    } else if ( start && !start.isValid() ){
      errors.startError = 'Start date is invalid.';
    }

    // Check if end date is valid
    if ( !end && props.isRequired ){
      errors.endError = 'End date is required.';
    } else if ( end && !end.isValid() ){
      errors.endError = 'End date is invalid.';
    }

    // Perform range validations if both dates are valid
    if ( start && end && start.isValid() && end.isValid() ){
      if ( start.isAfter(end) ){
        errors.rangeError = 'Start date cannot be after end date.';
      }
    }

    // Return null if there are no errors
    return Object.keys(errors).length > 0 ? errors : null;
  }
);

export const selectSortedInvoicesSummaryBreakdownByAccount = createSelector(
  [
    selectInvoicesSummaryBreakdownByAccount,
    selectInvoicesSummaryBreakdownByAccountSort
  ],
  ( breakdownByAccount, sort ) => {
    if ( !breakdownByAccount ) return null;

    return [...breakdownByAccount].sort((a, b) => {
      switch ( sort.sortBy ) {
        case 'company':
          return sortByName(a.account, b.account, 'company', sort.direction);
        case 'outstanding':
          return sortByNumber(a.breakdown.outstanding, b.breakdown.outstanding, 'amount', sort.direction);
        case 'overdue':
          return sortByNumber(a.breakdown.overdue, b.breakdown.overdue, 'amount', sort.direction);
        case 'upcoming':
          return sortByNumber(a.breakdown.upcoming, b.breakdown.upcoming, 'amount', sort.direction);
        default:
          return 0;
      }
    });
  }
);
